import React, { FC, Fragment, useEffect, useState } from 'react';
import ManageParticipantsDialog from 'components/shared/manage-participants/manage-participants-dialog.component';
import { Box, Button, Card, CardActions, CardContent } from '@mui/material';
import { LinkedClinic, LinkedUser } from 'models/order';
import ParticipantsList from 'components/shared/manage-participants/participants-list.component';
import { useTranslation } from 'react-i18next';
import HelpButton from 'components/shared/help-button/help-button.component';
import { Participants } from 'models/order-input/shared';

type OrderParticipantsProps = {
    participants: Participants;
    onParticipantsChanged?: (
        linkedDentists: LinkedUser[],
        linkedClinics: LinkedClinic[],
        customParticipantEmails: string[],
    ) => void;
};

const OrderParticipants: FC<OrderParticipantsProps> = ({ participants, onParticipantsChanged }) => {
    const { t: translate } = useTranslation(['orders']);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [currentLinkedDentists, setCurrentLinkedDentists] = useState<LinkedUser[]>([]);
    const [currentLinkedClinics, setCurrentLinkedClinics] = useState<LinkedClinic[]>([]);
    const [currentCustomEmails, setCurrentCustomEmails] = useState<string[]>([]);

    useEffect(() => {
        setCurrentLinkedDentists(participants.linkedDentists);
        setCurrentLinkedClinics(participants.linkedClinics);
        setCurrentCustomEmails(participants.customParticipantEmails);
    }, []);

    const handleParticipantsChange = (
        linkedDentists: LinkedUser[],
        linkedClinics: LinkedClinic[],
        customParticipantEmails: string[],
    ) => {
        setCurrentLinkedDentists(linkedDentists);
        setCurrentLinkedClinics(linkedClinics);
        setCurrentCustomEmails(customParticipantEmails);
        if (onParticipantsChanged) {
            onParticipantsChanged(linkedDentists, linkedClinics, customParticipantEmails);
        }
        setIsDialogOpen(false);
    };

    return (
        <Fragment>
            <Card variant="outlined" sx={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                    <ParticipantsList
                        linkedDentists={currentLinkedDentists}
                        linkedClinics={currentLinkedClinics}
                        customEmails={currentCustomEmails}
                    />
                </CardContent>
                <CardActions>
                    <Box display="flex" width="100%" alignItems="center">
                        <Box flexGrow={1}>
                            <Button
                                size="small"
                                onClick={() => setIsDialogOpen(true)}
                                data-test="order-participants.manage-btn"
                            >
                                {translate('orders:MANAGE_PARTICIPANTS')}
                            </Button>
                        </Box>
                        <Box ml="auto">
                            <HelpButton
                                title={translate('orders:PARTICIPANTS')}
                                helpText={translate('orders:PARTICIPANTS_HELP_TEXT')}
                            />
                        </Box>
                    </Box>
                </CardActions>
            </Card>
            <ManageParticipantsDialog
                orderLinkedDentists={currentLinkedDentists}
                orderLinkedClinics={currentLinkedClinics}
                orderCustomParticipantEmails={currentCustomEmails}
                onParticipantsChanged={handleParticipantsChange}
                onClose={() => setIsDialogOpen(false)}
                open={isDialogOpen}
            />
        </Fragment>
    );
};

export default OrderParticipants;
